/**
 * Global confirm / prompt dialog store.
 *
 * Promise-based, NON-BLOCKING replacements for the native
 * `window.confirm()` / `window.prompt()` dialogs. A native dialog blocks
 * the whole renderer until dismissed — in automated/headless sessions
 * the page never recovers (the Oct 12 "page freeze" family), and it
 * cannot be styled or stacked with the app's modal system.
 *
 * Usage:
 *   if (!(await confirmDialog('Delete this document?'))) return;
 *   const reason = await promptDialog('Reason for declining:');
 *   if (reason === null) return; // cancelled
 *
 * Rendered once at the application root by GlobalConfirmLayer (see
 * shared/ToastLayer.tsx), into document.body via a portal, above every
 * other layer. Only ONE dialog is shown at a time; further calls queue
 * in call order.
 */

export interface ConfirmRequest {
  id: string;
  kind: 'confirm' | 'prompt';
  message: string;
  /** Prompt only: initial input value. */
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface PendingRequest extends ConfirmRequest {
  resolve: (value: any) => void;
}

let current: PendingRequest | null = null;
const queue: PendingRequest[] = [];
const listeners = new Set<(req: ConfirmRequest | null) => void>();

function snapshotOf(req: PendingRequest): ConfirmRequest {
  return {
    id: req.id,
    kind: req.kind,
    message: req.message,
    defaultValue: req.defaultValue,
    confirmLabel: req.confirmLabel,
    cancelLabel: req.cancelLabel,
  };
}

function emit() {
  const snapshot: ConfirmRequest | null = current ? snapshotOf(current) : null;
  listeners.forEach((fn) => fn(snapshot));
}

function pump() {
  if (!current && queue.length > 0) {
    current = queue.shift() || null;
  }
  emit();
}

function enqueue<T>(req: Omit<ConfirmRequest, 'id'>): Promise<T> {
  return new Promise<T>((resolve) => {
    queue.push({
      ...req,
      id: `dlg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      resolve,
    });
    pump();
  });
}

/** Non-blocking replacement for window.confirm — resolves true on confirm. */
export function confirmDialog(
  message: string,
  opts?: { confirmLabel?: string; cancelLabel?: string },
): Promise<boolean> {
  return enqueue<boolean>({
    kind: 'confirm',
    message,
    confirmLabel: opts?.confirmLabel,
    cancelLabel: opts?.cancelLabel,
  });
}

/**
 * Non-blocking replacement for window.prompt — resolves the entered text
 * (possibly '') on confirm, or null when cancelled (same contract as the
 * native prompt).
 */
export function promptDialog(
  message: string,
  defaultValue = '',
  opts?: { confirmLabel?: string; cancelLabel?: string },
): Promise<string | null> {
  return enqueue<string | null>({
    kind: 'prompt',
    message,
    defaultValue,
    confirmLabel: opts?.confirmLabel,
    cancelLabel: opts?.cancelLabel,
  });
}

/** Called by the layer when the user answers (confirm) the current dialog. */
export function resolveCurrentDialog(value: any): void {
  if (!current) return;
  const req = current;
  current = null;
  req.resolve(value);
  pump();
}

/** Called by the layer when the user cancels/dismisses the current dialog. */
export function cancelCurrentDialog(): void {
  if (!current) return;
  const req = current;
  current = null;
  req.resolve(req.kind === 'prompt' ? null : false);
  pump();
}

/** Subscribe to the current dialog request. Returns an unsubscribe function. */
export function subscribeConfirmDialog(
  fn: (req: ConfirmRequest | null) => void,
): () => void {
  listeners.add(fn);
  fn(current ? snapshotOf(current) : null);
  return () => { listeners.delete(fn); };
}
